import { useState, useEffect } from 'react';
import { TrendingUp, Users, ShoppingBag, RefreshCw, Store, Truck, Star } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import api from '@/services/api';
import { formatCurrency } from '@/utils';
import { Skeleton } from '@/components/common/Skeleton';

interface AnalyticsData {
  topCustomers: { userId: string; totalSpend: number; orderCount: number; user?: { name: string; email: string } }[];
  orderTypeSplit: { type: string; count: number; revenue: number }[];
  categoryRevenue: { name: string; revenue: number }[];
  repeatOrderRate: number;
  weeklyRevenue: { week: string; revenue: number }[];
  activeSubscriptions: number;
}

const COLORS = ['#0D7377', '#F4A522', '#2ECC71', '#E74C3C', '#9B59B6', '#3498DB'];

export const OwnerAnalyticsPage = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/analytics/deep')
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-48" />)}
    </div>
  );
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
          <TrendingUp className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">Owner Analytics</h1>
          <p className="text-sm text-gray-500">Deep insights into your business performance</p>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-card shadow-card p-5">
          <Users className="h-5 w-5 text-primary-400 mb-2" />
          <p className="font-bold text-2xl text-gray-800">{data.repeatOrderRate}%</p>
          <p className="text-xs text-gray-500">Repeat Customer Rate</p>
        </div>
        <div className="bg-white rounded-card shadow-card p-5">
          <RefreshCw className="h-5 w-5 text-green-400 mb-2" />
          <p className="font-bold text-2xl text-gray-800">{data.activeSubscriptions}</p>
          <p className="text-xs text-gray-500">Active Subscriptions</p>
        </div>
        <div className="bg-white rounded-card shadow-card p-5">
          <Store className="h-5 w-5 text-accent-500 mb-2" />
          <p className="font-bold text-2xl text-gray-800">
            {data.orderTypeSplit.find(o => o.type === 'PICKUP')?.count || 0}
          </p>
          <p className="text-xs text-gray-500">Pickup Orders</p>
        </div>
        <div className="bg-white rounded-card shadow-card p-5">
          <Truck className="h-5 w-5 text-blue-400 mb-2" />
          <p className="font-bold text-2xl text-gray-800">
            {data.orderTypeSplit.find(o => o.type === 'DELIVERY')?.count || 0}
          </p>
          <p className="text-xs text-gray-500">Delivery Orders</p>
        </div>
      </div>

      {/* Weekly Revenue Chart */}
      {data.weeklyRevenue.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-6 mb-6">
          <h2 className="font-heading font-semibold text-gray-800 mb-4">Weekly Revenue (Last 12 Weeks)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.weeklyRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => formatCurrency(v)} labelFormatter={l => `Week of ${l}`} />
              <Line type="monotone" dataKey="revenue" stroke="#0D7377" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Category Revenue */}
        {data.categoryRevenue.length > 0 && (
          <div className="bg-white rounded-card shadow-card p-6">
            <h2 className="font-heading font-semibold text-gray-800 mb-4">Revenue by Category</h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.categoryRevenue.slice(0, 6)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Bar dataKey="revenue" fill="#0D7377" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Pickup vs Delivery Pie */}
        {data.orderTypeSplit.length > 0 && (
          <div className="bg-white rounded-card shadow-card p-6">
            <h2 className="font-heading font-semibold text-gray-800 mb-4">Pickup vs Delivery</h2>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.orderTypeSplit} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80} label={({ type, count }) => `${type}: ${count}`}>
                  {data.orderTypeSplit.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v: number, name: string) => [v, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top Customers */}
      <div className="bg-white rounded-card shadow-card p-6">
        <h2 className="font-heading font-semibold text-gray-800 mb-4">
          <Star className="h-4 w-4 inline text-accent-500 mr-2" />
          Top Customers
        </h2>
        <div className="space-y-3">
          {data.topCustomers.map((c, i) => (
            <div key={c.userId} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm shrink-0">
                {i + 1}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800 text-sm">{c.user?.name || 'Unknown'}</p>
                <p className="text-xs text-gray-400">{c.orderCount} orders</p>
              </div>
              <p className="font-bold text-primary-600">{formatCurrency(c.totalSpend)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
